import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'qapp.travel.dateSortDescending';
const EVENT_NAME = 'qapp-travel-date-sort-changed';

function readStored(defaultValue = true) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return defaultValue;
    return raw === 'true';
  } catch {
    return defaultValue;
  }
}

function writeStored(value) {
  try {
    localStorage.setItem(STORAGE_KEY, String(value));
  } catch {
    /* ignore quota / private mode */
  }
}

/**
 * Shared Newer/Older preference for Travel trip drawer (Overview places + Timeline days).
 * Persists in localStorage and syncs across mounted consumers via a window event.
 */
export default function useTravelDateSortDescending(defaultValue = true) {
  const [sortDescending, setSortDescendingState] = useState(() => readStored(defaultValue));

  useEffect(() => {
    const onCustom = (e) => {
      if (typeof e.detail === 'boolean') setSortDescendingState(e.detail);
    };
    const onStorage = (e) => {
      if (e.key !== STORAGE_KEY || e.newValue == null) return;
      setSortDescendingState(e.newValue === 'true');
    };
    window.addEventListener(EVENT_NAME, onCustom);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(EVENT_NAME, onCustom);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const setSortDescending = useCallback((updater) => {
    setSortDescendingState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      writeStored(next);
      window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: next }));
      return next;
    });
  }, []);

  return [sortDescending, setSortDescending];
}
